import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { SignOutButton } from '@clerk/clerk-react';
import { cn } from '@/lib/cn';
import type { CurrentUser } from '@/hook/useCurrentUser';
import { AvatarDisc } from './AvatarDisc';

interface AvatarMenuProps {
  user: CurrentUser;
  isAdmin: boolean;
}

const itemClass = 'block w-full px-4 py-[10px] text-left text-sm text-ink hover:bg-stripeA';

/** 28px avatar that opens Settings, the admin CMS for admins, and Sign out. */
export function AvatarMenu({ user, isAdmin }: AvatarMenuProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex rounded-full',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accFill focus-visible:ring-offset-2 focus-visible:ring-offset-bg'
        )}
      >
        <AvatarDisc user={user} size={28} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-9 z-40 min-w-[180px] overflow-hidden rounded-md border border-line bg-surface py-1"
        >
          <Link role="menuitem" to="/settings" onClick={() => setOpen(false)} className={itemClass}>
            Settings
          </Link>
          {isAdmin && (
            <>
              <Link role="menuitem" to="/admin/tracks" onClick={() => setOpen(false)} className={itemClass}>
                Admin
              </Link>
              <Link role="menuitem" to="/admin/packs" onClick={() => setOpen(false)} className={itemClass}>
                Packs
              </Link>
              <Link role="menuitem" to="/admin/demand" onClick={() => setOpen(false)} className={itemClass}>
                Demand
              </Link>
            </>
          )}
          <SignOutButton>
            <button type="button" role="menuitem" className={cn(itemClass, 'text-mute')}>
              Sign out
            </button>
          </SignOutButton>
        </div>
      )}
    </div>
  );
}
